import { useLayoutStyles } from "./styles";
import { Nav } from "react-bootstrap";
import "./Layout.css";
import {
    IsCauwUser,
    IsOrganizationDonor,
    IsOrganizationUser,
    hasPermission,
    hasDashboardPermission,
    hasRole,
    permissions,
    dashboardTypes,
    roles,
} from "../../utility/helpers/claimHelper";
import { EnvironmentSettings, IEnvironmentSettings } from "../../utility/EnvironmentHelper";
import { EnvironmentTypes } from "../../utility/Settings";

const SideBar = ({
    onNavigate,
    compact = false,
}: {
    onNavigate?: () => void;
    compact?: boolean;
}) => {
    const layoutStyles = useLayoutStyles();
    const navItemStyle = compact
        ? { ...layoutStyles.sidebarItem, fontSize: "0.95rem" }
        : layoutStyles.sidebarItem;
    const canViewReports = hasPermission([permissions.VIEW_REPORTS]);
    const canViewDashboards =
        hasDashboardPermission([dashboardTypes.LEADERSHIP]) ||
        hasDashboardPermission([dashboardTypes.ORGANIZATION]);
    const environmentSettings: IEnvironmentSettings = EnvironmentSettings();

    type LinkProps = {
        title: string;
        href: string;
        isDisplayed?: boolean;
        isDisabled?: boolean;
        isSeparator?: boolean;
        isSectionLabel?: boolean;
        separatorMarginTop?: string;
        separatorMarginBottom?: string;
        isIndented?: boolean;
    };

    const isProduction = environmentSettings.environment === EnvironmentTypes.prod;
    const leadershipDashboardId = isProduction ? "/leadership-dashboard/139c224f-bcfe-4a0e-a7e0-c3364c1b8eec" : "/leadership-dashboard/995c418d-7e18-49d8-9088-bb5241713c58";
    const employeeDashboardId = isProduction ? "/organization-dashboard/918748f3-28a4-4f91-b32f-8f8f3bb5365f" : "/organization-dashboard/ac439c65-ba32-4d72-89f3-89fdb8687d96";

    const links: LinkProps[] = [
        {
            //title: (userClaim && userClaim["organization_name"]) ?? "ExxonMobil",
            //href: userClaim && `/organization/${userClaim["organization_name"]}`,
            title: "ExxonMobil",
            href: "/organization/fb950126-da64-41af-cc54-08dc684063ed",
            isDisplayed:
                IsCauwUser() && hasPermission([permissions.MANAGE_ORGANIZATION]),
        },
        {
            title: "Active Campaigns",
            href: "/active-campaigns",
            isDisplayed: IsOrganizationDonor(),
        },
        {
            title: "Past Campaigns",
            href: "/past-campaigns",
            isDisplayed: IsOrganizationDonor(),
        },
        {
            title: "Campaigns",
            href: "/campaign",
            isDisplayed:
                IsOrganizationUser() &&
                !IsOrganizationDonor() &&
                hasPermission([permissions.MANAGE_CAMPAIGNS]),
        },
        {
            title: "Employees",
            href: "/employees",
            isDisplayed:
                IsOrganizationUser() &&
                !IsOrganizationDonor() &&
                hasPermission([permissions.MANAGE_EMPLOYEES]),
        },
        {
            title: "Users",
            href: "/users",
            isDisplayed:
                IsOrganizationUser() && hasRole([roles.ORGANIZATION_ADMIN]) && false,
        },
        {
            title: "Employee Upload",
            href: "/employee-upload",
            isDisplayed: hasPermission([permissions.IMPORT_EMPLOYEE_DATA]),
        },
        {
            title: "Organizations",
            href: "/organization",
            isDisplayed:
                hasPermission([permissions.MANAGE_ORGANIZATION, roles.SYSTEM_ADMIN]) &&
                false,
        },
        {
            title: "Settings",
            href: "/settings-page",
            isDisplayed: IsCauwUser(),
        },
        {
            isSeparator: true,
            title: "",
            href: "",
            isDisplayed: canViewReports,
            separatorMarginBottom: "0",
        },
        {
            title: "Reports",
            href: "/reports-page",
            isDisplayed: canViewReports,
        },
        {
            isSeparator: true,
            title: "",
            href: "",
            isDisplayed: canViewReports || canViewDashboards,
            separatorMarginTop:
                canViewReports && canViewDashboards ? "0" : "0.5rem",
            separatorMarginBottom: "0.5rem",
        },
        {
            title: "Dashboards",
            href: "",
            isSectionLabel: true,
            isDisplayed: canViewDashboards,
        },
        {
            title: "Leadership View",
            href: leadershipDashboardId,
            isDisplayed: hasDashboardPermission([dashboardTypes.LEADERSHIP]),
            isIndented: true,
        },
        {
            title: "Employee View",
            href: employeeDashboardId,
            isDisplayed: hasDashboardPermission([dashboardTypes.ORGANIZATION]),
            isIndented: true,
        },
    ];

    function GetNavItem(item: LinkProps, key: string): React.ReactNode | null {
        if (!item.isDisplayed && item.isDisplayed !== undefined) {
            return null;
        }

        if (item.isSeparator) {
            return GetNavSeparator(item, key);
        }

        if (item.isSectionLabel) {
            return GetNavSectionLabel(item, key);
        }

        return GetNavLink(item, key);
    }

    function GetNavSeparator(item: LinkProps, key: string) {
        return (
            <div key={key} className="nav-item px-2 py-0">
                <hr
                    style={{
                        marginTop: item.separatorMarginTop,
                        marginBottom: item.separatorMarginBottom,
                    }}
                />
            </div>
        )
    }

    function GetNavSectionLabel(item: LinkProps, key: string): React.ReactNode {
        return (
            <Nav.Item
                key={key}
                className="nav-link nav-section-label"
                style={navItemStyle}
            >
                {item.title}
            </Nav.Item>
        );
    }

    function GetNavLink(item: LinkProps, key: string): React.ReactNode {
      
        return (
            <div key={key}>
                {(item.isDisplayed || item.isDisplayed === undefined) && (
                    <Nav.Link
                        style={navItemStyle}
                        href={item.href}
                        onClick={onNavigate}
                        className={`${getBtnClassName(item.href)}${item.isIndented ? " nav-link-indented" : ""}`}
                    >
                        {item.title}
                    </Nav.Link>
                )}
            </div>
        )
    }

    function getBtnClassName(href: string) {
        const currentPath = window.location.pathname;
        if (
            href === "/active-campaigns" &&
            (currentPath === "/" || currentPath.includes(href)) &&
            IsOrganizationDonor()
        ) {
            return "nav_active base";
        }

        if (window.location.pathname.includes(href)) {
            return "nav_active base";
        }
        return "nav_btn";
    }

    return (
        <Nav className="d-flex flex-column" activeKey="/home">
            {links.map((item: LinkProps, index: number) => (
                GetNavItem(item, "sideNavItemMain-" + index)
            ))}
        </Nav>
    );
};

export default SideBar;
